import React, { useState } from 'react';
import {
  Tooltip,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  Button,
  Box,
  TextField
} from '@mui/material';
import {
  setAppetiteData,
  setTotalApplications,
  setCurrentPage
} from '../../store/ApplicationSlice';
import { useDispatch, useSelector } from 'react-redux';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { dateFilterData, intakeTableColumns } from '../../config/constant';
import { FilterAlt, FilterAltOutlined } from '@mui/icons-material';
import { getPriorityClass, truncateLob } from '../../utils/generalUtils';
import useApplicationData from '../../hooks/useApplicationData';
import {
  fetchDataForMultipleSelect,
  fetchFilterTotalApplications
} from '../../api/userService';
import FilterControls from './FilterControls';

const IntakeTable = ({
  setIsLoading,
  sortConfig,
  paginatedData,
  highlightedRow,
  handleSort,
  handleRowClick,
  handleRowDoubleClick,
  columnFilters,
  setColumnFilters,
  dateFilters,
  setDateFilters
}) => {
  const dispatch = useDispatch();
  const { fetchApplicationData, fetchTotalApplicationsCount } =
    useApplicationData();
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  const [selectOpen, setSelectOpen] = useState({}); // Manage open state per column
  const [searchTerm, setSearchTerm] = useState('');

  const uniqueValues = useSelector(state => state.intakeData.uniqueValues);

  // Handle filter change
  const handleFilterChange = async (column, selectedValues) => {
    if (selectedValues)
      setColumnFilters(prev => ({
        ...prev,
        [column]: selectedValues
      }));
  };

  const handleOnClearClick = async (column, selectedValues) => {
    setIsLoading(true);
    setSearchTerm('');
    if (column === 'date') {
      setDateFilters(dateFilterData);
    } else
      setColumnFilters(prev => ({
        ...prev,
        [column]: selectedValues
      }));
    await fetchApplicationData(accessToken, refreshToken);
    await fetchTotalApplicationsCount(accessToken, refreshToken);
    dispatch(setCurrentPage(1)); // Reset to first page when data changes
    setIsLoading(false);
  };

  const handleApplyClick = async column => {
    setIsLoading(true);
    const response = await fetchDataForMultipleSelect(
      columnFilters,
      dateFilters,
      accessToken,
      refreshToken
    );

    const totalCount = await fetchFilterTotalApplications(
      columnFilters,
      dateFilters,
      accessToken,
      refreshToken
    );

    if (response) {
      dispatch(setAppetiteData(response)); // Update Redux store
    }
    if (totalCount) {
      dispatch(setTotalApplications(totalCount.total));
    }
    dispatch(setCurrentPage(1)); // Reset to first page when data changes
    setIsLoading(false);
    setSelectOpen(prev => ({ ...prev, [column]: false })); // Close only the specific dropdown
    setSearchTerm('');
  };

  return (
    <div className='grid grid-cols-16 gap-4'>
      <div className='overflow-x-auto intake-table-data max-h-[474px] shadow-md rounded-lg'>
        <table className='min-w-full bg-white'>
          <thead className='bg-gray-200 table-header'>
            <tr>
              {intakeTableColumns.map(({ key, label }) => (
                <th key={key} className='text-left py-3 px-4 cursor-pointer'>
                  <div className='flex items-center space-x-2 min-w-max'>
                    <span onClick={() => handleSort(key)}>{label}</span>

                    {sortConfig.column === key &&
                      (sortConfig.direction === 'asc' ? (
                        <ArrowUpwardIcon />
                      ) : (
                        <ArrowDownwardIcon />
                      ))}

                    {/* Dropdown filter (only the arrow) */}

                    <Select
                      className='multipleSelect'
                      multiple
                      displayEmpty
                      value={columnFilters[key] || []}
                      size='small'
                      variant='standard'
                      disableUnderline
                      renderValue={() => null}
                      open={!!selectOpen[key]} // Use the column-specific state
                      onOpen={() =>
                        setSelectOpen(prev => ({ ...prev, [key]: true }))
                      }
                      onClose={
                        () => {
                          setSelectOpen(prev => ({ ...prev, [key]: false }));
                          setSearchTerm('');
                        } // Close all dropdowns
                      }
                      IconComponent={
                        selectOpen[key] ? FilterAlt : FilterAltOutlined
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300, // Fixed height
                            minWidth: 200,
                            borderRadius: 8,
                            boxShadow: '0px 4px 10px rgba(0,0,0,0.15)',
                            padding: '0px 8px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden' //Prevents extra scrollbar
                          }
                        }
                      }}
                    >
                      {/* Search Bar */}
                      {key !== 'date' ? (
                        <>
                          <TextField
                            value={searchTerm}
                            placeholder='Search...'
                            variant='standard'
                            fullWidth
                            onChange={e => setSearchTerm(e.target.value)}
                            InputProps={{ disableUnderline: true }}
                            sx={{
                              padding: '8px',
                              background: '#f5f5f5',
                              borderRadius: '4px'
                            }}
                          />

                          {/* ✅ Scrollable Options List */}
                          <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                            {uniqueValues &&
                              (uniqueValues[key] || [])
                                .filter(option =>
                                  (option?.toString() || '')
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase())
                                )
                                .map(option => (
                                  <MenuItem
                                    key={option}
                                    value={option}
                                    sx={{
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      maxWidth: '300px'
                                    }}
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <label
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%'
                                      }}
                                    >
                                      <Checkbox
                                        checked={(
                                          columnFilters[key] || []
                                        ).includes(option)}
                                        onChange={() => {
                                          const updatedFilters =
                                            columnFilters[key] || [];
                                          if (updatedFilters.includes(option)) {
                                            handleFilterChange(
                                              key,
                                              updatedFilters.filter(
                                                item => item !== option
                                              )
                                            );
                                          } else {
                                            handleFilterChange(key, [
                                              ...updatedFilters,
                                              option
                                            ]);
                                          }
                                        }}
                                      />
                                      <ListItemText
                                        primary={option}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </label>
                                  </MenuItem>
                                ))}
                          </Box>
                        </>
                      ) : (
                        <FilterControls
                          dateFilters={dateFilters}
                          setDateFilters={setDateFilters}
                        />
                      )}

                      {/* Sticky Footer Section */}
                      <Box
                        sx={{
                          position: 'sticky',
                          bottom: 0,
                          background: 'white',
                          borderTop: '1px solid #ddd',
                          padding: '8px',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => handleOnClearClick(key, [])}
                          sx={{
                            border: '1px solid #FF4D00',
                            color: '#FF4D00'
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          sx={{ backgroundColor: '#FF4D00', color: '#fff' }}
                          size='small'
                          variant='contained'
                          onClick={() => handleApplyClick(key)}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Select>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr
                key={row.application_id}
                className={`cursor-pointer ${highlightedRow === index ? 'active-row' : ''
                  }`}
                onClick={() =>
                  handleRowClick(index, row.application_id, row.customer_name)
                }
                onDoubleClick={() => handleRowDoubleClick(row.application_id)}
              >
                {Object.keys(row).map(key => (
                  <td
                    key={key}
                    className={`border px-4 py-2 capitalize${key === 'application_status' ? ' min-w-[125px]' : ''
                      }`}
                  >
                    {key === 'priority' ? (
                      <span
                        className={`${getPriorityClass(
                          row[key]
                        )} inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2`}
                      >
                        {row[key]}
                      </span>
                    ) : key === 'date' ? (
                      new Date(row[key]).toLocaleDateString('en-GB') // Format date
                    ) : key === 'lob' ? (
                      <Tooltip title={row[key]} arrow>
                        <span className='cursor-pointer'>
                          {truncateLob(row[key])}
                        </span>
                      </Tooltip>
                    ) : (
                      row[key]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IntakeTable;
